import React, { useContext, useEffect, Fragment } from 'react';

import { useMst } from '@trader/store';
import { TPlaceOrderSide } from '@trader/api';
import { TradingContext } from '@trader/contexts';
import { useI18next } from '@trader/services';
import {
  formatNumber,
  getRequiredOpenCostQuantity,
  getTotalValue,
} from '@trader/utils';
import { usePrevious, usePurchase } from '@trader/hooks';

import * as Styled from './styled';

export interface IPurchaseDetails {
  symbol: string;
  quantity: string;
  price?: string;
  amountVolume?: string;
  side: TPlaceOrderSide;
  addDeposit: () => void;
}

export const minimumPriceFractionDigits = 2;

export const PurchaseDetails: React.FC<IPurchaseDetails> = ({
  symbol,
  quantity,
  amountVolume,
  price = '0',
  side,
}) => {
  const {
    instrument: {
      general,
      market: { valueVolume, minAmountValue },
    },
    getRequiredOpenCost,
    requiredMargin,
    currencyConvert,
    isValidationErrors,
    isRequiredOpenCostInProgress,
    isInsufficientFundsError,
  } = useContext(TradingContext);
  const store = useMst();
  const purchase = usePurchase();
  const { translate } = useI18next();

  const currencySymbol: string = store.user.portfolio.currencySymbol;
  const amount = getRequiredOpenCostQuantity(quantity, minAmountValue);
  const prevAmount = usePrevious(amount);
  const prevPrice = usePrevious(price);

  useEffect(() => {
    const isSameInputsAfterFundsErrorAppeared =
      amount === prevAmount && price === prevPrice && isInsufficientFundsError;

    if (!isSameInputsAfterFundsErrorAppeared) {
      getRequiredOpenCost(symbol, side, amount, Number(price));
    }
  }, [amount, price, side]);

  const totalValue = getTotalValue(
    amountVolume ? (+amountVolume * +quantity).toString() : valueVolume,
    currencyConvert,
    general.currencySymbol,
    currencySymbol
  );

  const convertReqMargin = () => {
    if (currencyConvert !== 1) {
      const resultVolume = requiredMargin * currencyConvert;
      return `${general.currencySymbol}${formatNumber(
        resultVolume,
        minimumPriceFractionDigits
      )} = ${currencySymbol}${formatNumber(
        requiredMargin,
        minimumPriceFractionDigits
      )}`;
    }
    return `${general.currencySymbol}${formatNumber(
      requiredMargin,
      minimumPriceFractionDigits
    )}`;
  };

  const renderError = () => {
    if (
      isValidationErrors ||
      isRequiredOpenCostInProgress ||
      !isInsufficientFundsError
    ) {
      return;
    }

    const isShowDepositFunds =
      !purchase.shouldHideButtonOnCreateOrder && !purchase.isDisabled;

    return (
      <Fragment>
        <Styled.FundsTitle>
          {translate('COMMON.LABELS.INSUFFICIENT_FUNDS')}
        </Styled.FundsTitle>
        {isShowDepositFunds && (
          <Styled.FundsButton onClick={purchase.action}>
            {translate('COMMON.LABELS.DEPOSIT_FUNDS')}
          </Styled.FundsButton>
        )}
      </Fragment>
    );
  };

  return (
    <Styled.Root>
      <Styled.TextWrapper>
        <Styled.TotalValue>
          {translate('COMMON.LABELS.TOTAL_VALUE')}: {totalValue}
        </Styled.TotalValue>
      </Styled.TextWrapper>
      {!import.meta.env.VITE_IS_DMA && (
        <Styled.TextWrapper>
          <Styled.RequiredMarginTitle>
            {translate('COMMON.LABELS.REQUIRED_MARGIN')}:
          </Styled.RequiredMarginTitle>
          <Styled.RequiredMarginValue>
            {quantity ? convertReqMargin() : 0}
          </Styled.RequiredMarginValue>
        </Styled.TextWrapper>
      )}
      <Styled.TextWrapper mt={3}>{renderError()}</Styled.TextWrapper>
    </Styled.Root>
  );
};
